<!-- 瀑布流 -->
<template>
  <div class="waterfall-container" ref="containerRef">
    <!-- 瀑布流容器 -->
    <div class="waterfall-wrapper" :style="{ height: containerHeight + 'px' }">
      <!-- 虚拟化渲染的列 -->
      <div
        v-for="(column, columnIndex) in visibleColumns"
        :key="columnIndex"
        class="waterfall-column"
        :style="{
          left:
            columnIndex * actualItemWidth + columnIndex * gapItemWidth + 'px',
        }"
      >
        <div
          v-for="item in column.items"
          :key="item.id"
          class="waterfall-item"
          :data-index="item.originalIndex"
          :data-item-id="item.id"
          :style="{
            position: 'absolute',
            top: (item.top || 0) + 'px',
            width: '100%',
          }"
        >
          <!-- 🔧 懒加载：只有进入视口或附近区域才渲染内容 -->
          <slot 
            v-if="isItemVisible(String(item.id))" 
            name="item" 
            :item="item" 
            :index="item.originalIndex"
            :is-visible="isItemVisible(String(item.id))"
          >
            <!-- 默认卡片内容 -->
            <div class="default-card">
              <!-- <img v-if="item.image" :src="item.image" :alt="item.title" /> -->
              <div class="card-content">
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
              </div>
            </div>
          </slot>
          <!-- 骨架屏占位 -->
          <div v-else class="waterfall-item-skeleton">
            <div class="skeleton-placeholder"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态指示器 -->
    <div v-if="loading" class="loading-indicator">
      <div class="loading-spinner"></div>
      <span>{{ t("loading") }}</span>
    </div>

    <!-- 空状态 -->
    <div v-if="props.items.length === 0 && !props.loading" class="empty-state">
      <slot name="empty"> </slot>
    </div>

    <!-- 滚动到顶部按钮 -->
    <div
      v-if="showScrollTop"
      class="scroll-top-btn w-52px h-52px"
      @click="scrollToTop"
      :title="t('back_to_top')"
    >
      <div class="w-40px h-40px">
        <img src="@/assets/images/home/up.webp" alt="" class="w-full h-full" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import { theme } from '@/styles/theme';
// import { useWindowResize } from "~/utils/windowResize";


interface WaterfallItem {
  id: string | number;
  title?: string;
  description?: string;
  image?: string;
  originalIndex: number;
  top?: number;
  [key: string]: any;
}

interface WaterfallColumn {
  items: WaterfallItem[];
  height: number;
}

interface Props {
  items: WaterfallItem[];
  columns?: number;
  gap?: number;
  itemWidth?: number;
  threshold?: number;
  loading?: boolean;
  hasMore?: boolean;
  autoLoad?: boolean;
  leftFirst?: boolean; // 是否优先从左侧填充
  heightTolerance?: number; // 高度差容忍度
}

interface Emits {
  (e: "loadMore"): void;
  (e: "itemClick", item: WaterfallItem, index: number): void;
  (e: "scroll", event: Event): void;
}

const props = withDefaults(defineProps<Props>(), {
  columns: 8, // 最大列数
  gap: 24,
  itemWidth: 246, // 与CustomCard宽度匹配
  threshold: 100,
  loading: false,
  hasMore: true,
  autoLoad: true,
  leftFirst: true, // 默认优先从左侧填充
  heightTolerance: 50, // 默认高度差容忍度50px
});

const { t } = useI18n();

const emit = defineEmits<Emits>();

// 使用窗口宽度监听
// const { windowWidth } = useWindowResize();

// 响应式数据
const containerRef = ref<HTMLElement>();
const containerHeight = ref(0);
const scrollTop = ref(0);
const showScrollTop = ref(false);
const itemHeights = ref<Map<string, number>>(new Map());
const resizeObserver = ref<ResizeObserver | null>(null);
// 屏幕宽度响应式状态
const screenWidth = ref(0);

// 🔧 懒加载相关状态
const visibleItems = ref<Set<string>>(new Set()); // 当前可见的项目ID
const intersectionObserver = ref<IntersectionObserver | null>(null);
const observedElements = ref<Map<string, HTMLElement>>(new Map()); // 已观察的元素

// 根据屏幕宽度计算 gap 值
const gapItemWidth = computed(() => {
  const width = screenWidth.value;
  // console.log("width", width);
  let gapValue;

  if (width >= 1920) {
    gapValue = 48; // 超大屏幕
  } else if (width >= 1440) {
    gapValue = 36; // 大屏幕
  } else if (width >= 1200) {
    gapValue = 28; // 中等屏幕
  } else if (width >= 992) {
    gapValue = 24; // 小屏幕
  } else if (width >= 768) {
    gapValue = 20; // 平板
  } else {
    gapValue = 16; // 手机
  }

  return gapValue;
});

// 更新屏幕宽度
const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth;
};


// 容器宽度的响应式引用
const containerWidthRef = ref(0);

// 更新容器宽度
const updateContainerWidth = () => {
  if (containerRef.value) {
    const newWidth = containerRef.value.clientWidth;
    if (newWidth > 0) {
      containerWidthRef.value = newWidth;
      return;
    }
  }
  
  // 当容器还没渲染时，直接使用屏幕宽度减去padding
  const padding = 40; // 左右padding (20px * 2)
  containerWidthRef.value = Math.max(0, screenWidth.value - padding);
};

// 计算属性
const containerWidth = computed(() => {
  // 依赖响应式的容器宽度
  return containerWidthRef.value || screenWidth.value - 40;
});

// 移除了固定宽度ref，现在使用响应式计算

// 响应式计算项目宽度：基于容器宽度和列数自适应
const actualItemWidth = computed(() => {
  const width = containerWidth.value;
  const columns = props.columns;
  const gap = props.gap;
  
  if (width === 0) return 159; // 默认宽度
  
  // 计算每列的宽度：(容器宽度 - (列数-1)*间隙) / 列数
  const itemWidth = Math.floor((width - (columns - 1) * gap) / columns);
  
  // 确保宽度不会太小
  return Math.max(100, itemWidth);
});

const actualColumns = computed(() => {
  // 直接使用传入的 columns 参数，实现固定列数
  return props.columns;
});

const visibleColumns = computed(() => {
  if (props.items.length === 0) return [];

  const columns: WaterfallColumn[] = Array.from(
    { length: actualColumns.value },
    () => ({
      items: [],
      height: 0,
    })
  );
  
  // 使用瀑布流算法，总是将项目放到最短的列中
  props.items.forEach((item, index) => {
    // 智能选择列：根据配置决定是否优先从左侧填充
    const shortestColumnIndex = columns.reduce(
      (minIndex, column, currentIndex) => {
        const minColumn = columns[minIndex];
        if (!minColumn) return currentIndex;

        if (!props.leftFirst) {
          // 传统瀑布流：总是选择最短列
          return column.height < minColumn.height ? currentIndex : minIndex;
        }

        const heightDiff = Math.abs(column.height - minColumn.height);

        // 如果当前列明显更短，选择它
        if (column.height < minColumn.height - props.heightTolerance) {
          return currentIndex;
        }
        // 如果高度相近（差异在容忍度内），优先选择左侧（索引更小的）
        else if (
          heightDiff <= props.heightTolerance &&
          currentIndex < minIndex
        ) {
          return currentIndex;
        }
        // 否则保持原选择
        return minIndex;
      },
      0
    );

    // 获取项目的实际高度，如果没有则使用默认高度
    const itemId = String(item.id);
    const actualItemHeight = itemHeights.value.get(itemId) || 300;

    const targetColumn = columns[shortestColumnIndex];
    if (targetColumn) {
      targetColumn.items.push({
        ...item,
        originalIndex: index,
        top: targetColumn.height,
      });

      // 更新列高度
      targetColumn.height += actualItemHeight + gapItemWidth.value;
    }
  });

  return columns;
});

// 滚动处理
let scrollHandler: (() => void) | null = null;
let resizeHandler: (() => void) | null = null;

const handleScroll = () => {
  if (!containerRef.value) return;

  // 查找父级滚动容器
  const parentScrollContainer = findParentScrollContainer(containerRef.value);
  let scrollTopValue = 0;
  let scrollHeight = 0;
  let clientHeight = 0;

  if (parentScrollContainer) {
    scrollTopValue = parentScrollContainer.scrollTop;
    scrollHeight = parentScrollContainer.scrollHeight;
    clientHeight = parentScrollContainer.clientHeight;
  } else {
    // 使用window的滚动信息
    scrollTopValue = window.scrollY;
    scrollHeight = document.documentElement.scrollHeight;
    clientHeight = window.innerHeight;
  }

  scrollTop.value = scrollTopValue;

  // 显示/隐藏回到顶部按钮
  showScrollTop.value = scrollTopValue > 300;

  // 检查是否需要加载更多
  // 当滚动到距离底部阈值时触发加载更多
  if (scrollTopValue + clientHeight + props.threshold >= scrollHeight) {
    emit("loadMore");
  }

  emit("scroll", new Event("scroll"));
};

const handleResize = () => {
  updateScreenWidth(); // 更新屏幕宽度
  updateContainerWidth(); // 更新容器宽度
  
  // 强制重新计算布局
  nextTick(() => {
    // 清空项目高度缓存，强制重新计算
    itemHeights.value.clear();
    
    // 重新测量和布局
    measureItemHeights();
    updateContainerHeight();
    
    // 🔧 重新设置懒加载观察器
    setTimeout(() => {
      setupIntersectionObserver();
    }, 150);
    
    // console.log("🔄 窗口大小变化，重新布局:", {
    //   screenWidth: screenWidth.value,
    //   containerWidth: containerWidth.value,
    //   actualItemWidth: actualItemWidth.value,
    //   actualColumns: actualColumns.value,
    // });
  });
};

// 测量项目高度并重新布局
const measureItemHeights = () => {
  if (!containerRef.value) return;

  let hasNewMeasurements = false;
  const items = containerRef.value.querySelectorAll(".waterfall-item");

  items.forEach((element) => {
    const itemIndex = element.getAttribute("data-index");
    if (itemIndex !== null) {
      const item = props.items[parseInt(itemIndex)];
      if (item) {
        const itemId = String(item.id);
        const currentHeight = itemHeights.value.get(itemId);
        const actualHeight = element.getBoundingClientRect().height;

        if (currentHeight !== actualHeight && actualHeight > 0) {
          itemHeights.value.set(itemId, actualHeight);
          hasNewMeasurements = true;
        }
      }
    }
  });

  // 如果有新的测量结果，触发重新布局
  if (hasNewMeasurements) {
    nextTick(() => {
      updateContainerHeight();
    });
  }
};

// 设置ResizeObserver来监听项目高度变化
const setupResizeObserver = () => {
  if (!containerRef.value || !window.ResizeObserver) return;

  resizeObserver.value = new ResizeObserver((entries) => {
    let needsUpdate = false;

    entries.forEach((entry) => {
      const element = entry.target as HTMLElement;
      const itemIndex = element.getAttribute("data-index");

      if (itemIndex !== null) {
        const item = props.items[parseInt(itemIndex)];
        if (item) {
          const itemId = String(item.id);
          const newHeight = entry.contentRect.height;
          const currentHeight = itemHeights.value.get(itemId);

          if (currentHeight !== newHeight && newHeight > 0) {
            itemHeights.value.set(itemId, newHeight);
            needsUpdate = true;
          }
        }
      }
    });

    if (needsUpdate) {
      nextTick(() => {
        updateContainerHeight();
      });
    }
  });

  // 观察所有瀑布流项目
  const items = containerRef.value.querySelectorAll(".waterfall-item");
  items.forEach((item) => {
    resizeObserver.value?.observe(item);
  });
};

// 更新容器高度
const updateContainerHeight = () => {
  if (visibleColumns.value.length === 0) {
    containerHeight.value = 0;
    return;
  }

  // 找到最高的列高度
  const maxHeight = Math.max(...visibleColumns.value.map((col) => col.height));
  containerHeight.value = maxHeight;
};

// 🔧 懒加载：判断项目是否可见（或即将可见）
const isItemVisible = (itemId: string): boolean => {
  return visibleItems.value.has(itemId);
};

// 🔧 懒加载：设置 Intersection Observer
const setupIntersectionObserver = () => {
  if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
    // 如果不支持 IntersectionObserver，则显示所有项目
    props.items.forEach(item => {
      visibleItems.value.add(String(item.id));
    });
    return;
  }

  // 清理旧的观察器
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
  }

  // 创建新的观察器，带有预加载缓冲区
  // rootMargin: 提前 300px 开始加载（上下各 300px）
  intersectionObserver.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const element = entry.target as HTMLElement;
        const itemId = element.getAttribute('data-item-id');
        
        if (itemId) {
          if (entry.isIntersecting) {
            // 进入视口或缓冲区，标记为可见
            visibleItems.value.add(itemId);
            console.log('📥 加载项目:', itemId);
          } else {
            // 离开视口很远时，可以选择性地移除（但保留已加载的）
            // 这里我们保留已经加载的项目，避免重复加载
            // visibleItems.value.delete(itemId);
          }
        }
      });
    },
    {
      root: findParentScrollContainer(containerRef.value),
      rootMargin: '300px 0px', // 提前 300px 预加载
      threshold: 0.01, // 只要有 1% 进入视口就触发
    }
  );

  // 观察所有瀑布流项目
  nextTick(() => {
    const items = containerRef.value?.querySelectorAll('.waterfall-item');
    items?.forEach((item) => {
      const itemId = item.getAttribute('data-item-id');
      if (itemId) {
        intersectionObserver.value?.observe(item);
        observedElements.value.set(itemId, item as HTMLElement);
      }
    });
  });
};

// 🔧 懒加载：清理 Intersection Observer
const cleanupIntersectionObserver = () => {
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
    intersectionObserver.value = null;
  }
  observedElements.value.clear();
};

// 滚动到顶部
const scrollToTop = () => {
  // 查找父级滚动容器
  const parentScrollContainer = findParentScrollContainer(containerRef.value!);

  if (parentScrollContainer) {
    // 如果有父级滚动容器，滚动到父级容器的顶部
    parentScrollContainer.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  } else {
    // 如果没有父级滚动容器，滚动到 window 的顶部
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
};

// 滚动到指定项目
const scrollToItem = (index: number) => {
  if (!containerRef.value) return;

  const itemElement = containerRef.value.querySelector(
    `[data-index="${index}"]`
  ) as HTMLElement;
  if (itemElement) {
    itemElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
};

// 暴露方法
defineExpose({
  scrollToTop,
  scrollToItem,
  updateContainerHeight,
  measureItemHeights,
});

// 生命周期
onMounted(() => {
  // 初始化屏幕宽度和容器宽度
  updateScreenWidth();
  updateContainerWidth();

  // 监听窗口大小变化
  window.addEventListener("resize", handleResize);
  if (containerRef.value) {
    scrollHandler = handleScroll;
    resizeHandler = handleResize;

    // 查找父级滚动容器
    const parentScrollContainer = findParentScrollContainer(containerRef.value);

    if (parentScrollContainer) {
      parentScrollContainer.addEventListener("scroll", scrollHandler);
    } else {
      // 如果没有找到父级滚动容器，使用window的滚动
      window.addEventListener("scroll", scrollHandler);
    }

    window.addEventListener("resize", resizeHandler);

    nextTick(() => {
      updateContainerHeight();
      // 延迟测量高度，确保DOM完全渲染
      setTimeout(() => {
        measureItemHeights();
        setupResizeObserver();
        // 🔧 设置懒加载观察器
        setupIntersectionObserver();
      }, 100);
    });
  }
});

// 查找父级滚动容器
const findParentScrollContainer = (
  element: HTMLElement
): HTMLElement | null => {
  let parent = element.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    if (
      style.overflow === "auto" ||
      style.overflow === "scroll" ||
      style.overflowY === "auto" ||
      style.overflowY === "scroll"
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

onUnmounted(() => {
  if (containerRef.value && scrollHandler) {
    // 查找父级滚动容器
    const parentScrollContainer = findParentScrollContainer(containerRef.value);

    if (parentScrollContainer) {
      parentScrollContainer.removeEventListener("scroll", scrollHandler);
    } else {
      window.removeEventListener("scroll", scrollHandler);
    }
  }
  if (resizeHandler) {
    window.removeEventListener("resize", resizeHandler);
  }

  // 清理ResizeObserver
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
  // 🔧 清理 IntersectionObserver
  cleanupIntersectionObserver();
  // 清理窗口大小变化监听
  window.removeEventListener("resize", handleResize);
});

// 监听数据变化 - 移除深度监听，只监听数组长度变化
watch(
  () => props.items.length,
  () => {
    nextTick(() => {
      updateContainerHeight();
      // 数据变化后重新测量高度和设置观察器
      setTimeout(() => {
        measureItemHeights();
        // 重新设置ResizeObserver
        if (resizeObserver.value) {
          resizeObserver.value.disconnect();
        }
        setupResizeObserver();
        // 🔧 重新设置 IntersectionObserver
        setupIntersectionObserver();
      }, 100);
    });
  }
);

watch(
  () => props.columns,
  () => {
    nextTick(() => {
      updateContainerHeight();
    });
  }
);

// 监听列数据变化，自动更新容器高度 - 移除深度监听
watch(
  () => visibleColumns.value.length,
  () => {
    nextTick(() => {
      updateContainerHeight();
    });
  }
);

// 监听实际项目宽度变化，重新布局
watch(actualItemWidth, (newWidth, oldWidth) => {
  if (newWidth !== oldWidth) {
    // console.log("📏 项目宽度变化，重新布局:", {
    //   oldWidth,
    //   newWidth,
    //   propsWidth: props.itemWidth,
    // });

    nextTick(() => {
      updateContainerHeight();
      setTimeout(() => {
        measureItemHeights();
      }, 50);
    });
  }
});

// 监听实际列数变化，重新布局
watch(actualColumns, (newColumns, oldColumns) => {
  if (newColumns !== oldColumns) {

    nextTick(() => {
      updateContainerHeight();
      // 重新测量项目高度，确保布局正确
      setTimeout(() => {
        measureItemHeights();
      }, 50);
    });
  }
});

// 监听项目宽度变化，重新布局
watch(actualItemWidth, (newWidth, oldWidth) => {
  if (newWidth !== oldWidth) {

    nextTick(() => {
      updateContainerHeight();
      // 重新测量项目高度，确保布局正确
      setTimeout(() => {
        measureItemHeights();
      }, 50);
    });
  }
});

</script>

<style lang="scss" scoped>
.waterfall-container {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
}

.waterfall-wrapper {
  position: relative;
  width: 100%;
  min-height: 100%;
}

.waterfall-column {
  position: absolute;
  width: v-bind("actualItemWidth + 'px'");
}

.waterfall-item {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  // margin-bottom: v-bind("gapItemWidth + 'px'");

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
}

// 🔧 骨架屏占位符
.waterfall-item-skeleton {
  width: 100%;
  min-height: 300px; // 默认最小高度
  background: v-bind('theme.colors.background');
  border-radius: 12px;
  overflow: hidden;
  position: relative;

  .skeleton-placeholder {
    width: 100%;
    height: 100%;
    min-height: 300px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.06) 25%,
      rgba(255, 255, 255, 0.12) 50%,
      rgba(255, 255, 255, 0.06) 75%
    );
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.5s infinite;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.default-card {
  padding: 16px;

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    object-position: top;

    border-radius: 8px;
  }

  .card-content {
    padding: 12px 0;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }

    p {
      margin: 0;
      font-size: 14px;
      color: #666;
      line-height: 1.4;
    }
  }
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #666;

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
  }
}

.no-more-data {
  text-align: center;
  padding: 32px;
  color: #999;
  font-size: 14px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
  font-size: 14px;
}

.scroll-top-btn {
  position: fixed;
  bottom: 62px;
  right: 22px;
  width: 50px;
  height: 50px;
  background: v-bind('theme.colors.backgroundSecondary');
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  z-index: 1000;
  // border: 1px solid v-bind('theme.colors.border');

  svg {
    width: 24px;
    height: 24px;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .waterfall-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .waterfall-wrapper {
    grid-template-columns: 1fr;
  }
}
</style>
